import React, { useEffect, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { createRoot } from 'react-dom/client';
import { AlertTriangle, HelpCircle, Info } from 'lucide-react';
import '@/styles/tiktok-platform/tiktok-confirm-dialog.css';
import '@/styles/shared/modal-footer-confirm-button.css';

const TONE_META = {
  default: {
    icon: HelpCircle,
    confirmClass: 'tiktok-confirm__btn--primary',
  },
  warning: {
    icon: AlertTriangle,
    confirmClass: 'tiktok-confirm__btn--warning',
  },
  danger: {
    icon: AlertTriangle,
    confirmClass: 'tiktok-confirm__btn--danger',
  },
  info: {
    icon: Info,
    confirmClass: 'tiktok-confirm__btn--primary',
  },
};

/**
 * TikTok 平台通用确认弹窗（声明式）。
 * 删除 / 撤回 / 取消等二次确认统一复用此组件。
 */
export default function TiktokConfirmDialog({
  open,
  title,
  message,
  children,
  tone = 'warning',
  confirmText,
  cancelText,
  confirming = false,
  confirmDisabled = false,
  closeOnBackdrop = true,
  wide = false,
  onConfirm,
  onCancel,
}) {
  const { t } = useTranslation('workbench');
  const resolvedTitle = title || t('moduleShell.confirmTitle');
  const resolvedConfirm = confirmText || t('moduleShell.confirmOk');
  const resolvedCancel = cancelText || t('shop.cancel');
  const reactId = useId();
  const titleId = `tiktok-confirm-title-${reactId.replace(/:/g, '')}`;
  const toneKey = TONE_META[tone] ? tone : 'warning';
  const { icon: Icon, confirmClass } = TONE_META[toneKey];

  useEffect(() => {
    if (!open) return undefined;
    let onKeyDown = null;
    const armTimer = window.setTimeout(() => {
      onKeyDown = (event) => {
        if (event.key === 'Escape') {
          if (!confirming) onCancel?.();
          return;
        }
        if (event.key === 'Enter' && !confirmDisabled && !confirming) {
          onConfirm?.();
        }
      };
      window.addEventListener('keydown', onKeyDown);
    }, 0);
    return () => {
      window.clearTimeout(armTimer);
      if (onKeyDown) window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, confirming, confirmDisabled, onCancel, onConfirm]);

  if (!open) return null;

  const body = children ?? (message ? (
    typeof message === 'string'
      ? <p className="tiktok-confirm__message">{message}</p>
      : <div className="tiktok-confirm__message">{message}</div>
  ) : null);

  return createPortal(
    <div className="tiktok-confirm" role="presentation">
      <button
        type="button"
        className="tiktok-confirm__backdrop"
        aria-label={t('moduleShell.close')}
        disabled={confirming || !closeOnBackdrop}
        onClick={() => {
          if (closeOnBackdrop && !confirming) onCancel?.();
        }}
      />
      <div
        className={`tiktok-confirm__panel tiktok-confirm__panel--${toneKey}${wide ? ' tiktok-confirm__panel--wide' : ''}`}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="tiktok-confirm__hero">
          <span className={`tiktok-confirm__icon tiktok-confirm__icon--${toneKey}`} aria-hidden="true">
            <Icon size={22} strokeWidth={2.1} />
          </span>
          <div className="tiktok-confirm__copy">
            <h3 id={titleId} className="tiktok-confirm__title">{resolvedTitle}</h3>
            {body}
          </div>
        </div>
        <footer className="tiktok-confirm__footer">
          <button
            type="button"
            className="tiktok-confirm__btn tiktok-confirm__btn--cancel"
            disabled={confirming}
            onClick={onCancel}
          >
            {resolvedCancel}
          </button>
          <button
            type="button"
            className={`tiktok-confirm__btn ${confirmClass}`}
            disabled={confirmDisabled || confirming}
            onClick={onConfirm}
          >
            {confirming ? t('moduleShell.processing') : resolvedConfirm}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

/**
 * 命令式确认：返回 Promise（确认 true / 取消 false）。
 * 适合现有 async/await 调用点直接替换 showConfirmToast。
 *
 * @example
 * const ok = await showTiktokConfirm({
 *   title: '确认撤回',
 *   message: '撤回后需重新发布。',
 *   confirmText: '撤回',
 *   tone: 'warning',
 * });
 */
export function showTiktokConfirm({
  title,
  message,
  tone = 'warning',
  confirmText,
  cancelText,
  closeOnBackdrop = true,
  wide = false,
} = {}) {
  return new Promise((resolve) => {
    const container = document.createElement('div');
    document.body.appendChild(container);
    const root = createRoot(container);
    const cleanup = () => {
      root.unmount();
      if (container.parentNode) container.parentNode.removeChild(container);
    };
    const finish = (value) => {
      cleanup();
      resolve(value);
    };
    root.render(
      <TiktokConfirmDialog
        open
        title={title}
        message={message}
        tone={tone}
        confirmText={confirmText}
        cancelText={cancelText}
        closeOnBackdrop={closeOnBackdrop}
        wide={wide}
        onCancel={() => finish(false)}
        onConfirm={() => finish(true)}
      />,
    );
  });
}
